import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CompletionRequest, CompletionMessage } from '@app-libs/model';
import { StreamingApiClient } from '../client/streaming-api-client';
import { HttpClient } from '@angular/common/http';
import { ConfigurationClientService } from '../client/configuration-client.service';

@Injectable({
  providedIn: 'root',
})
export class OpenaiAPIService extends StreamingApiClient {
  private readonly basePath = '/openai'; // Base URL for the OpenAI backend
  private readonly assistantPath = '/assistant'; // Base URL for the OpenAI assistant

  constructor(
    protected override config: ConfigurationClientService,
    protected override http: HttpClient,
  ) {
    super(config, http);
  }

  override get baseUrl(): string {
    return this.config.getBackendAPIUrl() + this.basePath;
  }

  get assistantUrl(): string {
    return this.baseUrl + this.assistantPath;
  }

  /**
   * Unified completion method - Phase 4 implementation
   *
   * Calls the new /openai/completion endpoint that consolidates all frontend logic:
   * - Chat type detection (assistant vs completion)
   * - Parameter transformation (legacy vs unified API)
   * - Feature flag logic
   *
   * Reduces frontend complexity by ~70% while maintaining 100% compatibility.
   */
  createCompletion(request: CompletionRequest): Observable<any> {
    console.debug('Calling createCompletion with request:', request);
    const url = `${this.baseUrl}/completion`;
    // Always streaming for unified endpoint
    return this.createStreamingRequest(url, request);
  }

  // Creates a plain-text non-streaming completion.
  createCompletionAsText(
    prompt: string,
    messages: Partial<CompletionMessage>[],
    model: string,
  ): Observable<string> {
    const url = `${this.baseUrl}/completion/text`;
    return this.http.post(url, { prompt, messages, model }, { responseType: 'text' });
  }

  // Runs an existing thread and streams the result.
  runMessageThread(assistantId: string, threadId: string): Observable<any> {
    const url = `${this.assistantUrl}/thread/run`;
    return this.createStreamingRequest(url, { assistantId, threadId });
  }

  // Creates and runs a new thread.
  createThreadAndRun(assistantId: string, prompt: string, messages: CompletionMessage[]): Observable<any> {
    const url = `${this.assistantUrl}/thread/create-run`;
    return this.createStreamingRequest(url, {
      assistantId,
      prompt,
      messages,
    });
  }

  // Creates a message and runs a thread.
  createMessageAndRunThread(
    assistantId: string,
    threadId: string,
    message: CompletionMessage,
    instruction?: string,
  ): Observable<any> {
    const url = `${this.assistantUrl}/${assistantId}/thread/${threadId}/message/run`;
    return this.createStreamingRequest(url, { ...message, instruction });
  }

  // Creates a new thread.
  createThread(): Observable<any> {
    const url = `${this.assistantUrl}/thread/create`;
    return this.http.post<any>(url, {});
  }

  // Creates a message in a thread.
  createThreadMessage(threadId: string, message: CompletionMessage): Observable<any> {
    const url = `${this.assistantUrl}/thread/${threadId}/message`;
    return this.http.post<any>(url, message);
  }

  // Uploads a file to OpenAI for use in completions
  uploadFileToOpenAI(file: File): Observable<{ fileId: string; correlationId: string }> {
    console.debug('Uploading file to OpenAI:', file.name, file.type);
    const url = `${this.baseUrl}/files/upload`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ fileId: string; correlationId: string }>(url, formData);
  }

  // Transcribes audio using OpenAI Whisper.
  transcribeAudio(audioBlob: Blob): Observable<string> {
    console.debug('Transcribing audio:', audioBlob.type);
    const url = `${this.baseUrl}/audio/transcribe`;
    const formData = new FormData();
    const format = this.config.getSetting('fileUploads', 'audioCaptureFormat') || 'webm';
    formData.append('audioBlob', audioBlob, 'audio.' + format);
    return this.http.post(url, formData, {
      responseType: 'text',
    });
  }
}
